import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createSupportApi,
  replySupportApi,
  getRestaurantTicketsApi,
  updateTicketStatusApi
} from "../../api/supportApi";

// CREATE TICKET
export const createSupport = createAsyncThunk(
  "support/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createSupportApi(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create support ticket");
    }
  }
);

// GET TICKETS
export const getRestaurantTickets = createAsyncThunk(
  "support/getTickets",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getRestaurantTicketsApi(restaurantId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch tickets");
    }
  }
);

// REPLY TICKET
export const replySupportTicket = createAsyncThunk(
  "support/reply",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const response = await replySupportApi(ticketId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to reply to ticket");
    }
  }
);

// UPDATE STATUS
export const updateTicketStatus = createAsyncThunk(
  "support/updateStatus",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const response = await updateTicketStatusApi(ticketId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update ticket status");
    }
  }
);

const supportSlice = createSlice({
  name: "support",
  
  initialState: {
    tickets: [],
    loading: false,
    error: null,
    selectedTicket: null, // Added for better state management
  },
  
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedTicket: (state, action) => {
      state.selectedTicket = action.payload;
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
  },
  
  extraReducers: (builder) => {
    // Create Support
    builder
      .addCase(createSupport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupport.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new ticket to the list if it's returned in the response
        if (action.payload?.data) {
          state.tickets = [action.payload.data, ...state.tickets];
        }
      })
      .addCase(createSupport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
    
    // Get Tickets
      .addCase(getRestaurantTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRestaurantTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload?.data || [];
      })
      .addCase(getRestaurantTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.tickets = [];
      })
    
    // Reply to Ticket
      .addCase(replySupportTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replySupportTicket.fulfilled, (state, action) => {
        state.loading = false;
        // Update the ticket with new reply
        if (action.payload?.data) {
          const index = state.tickets.findIndex(ticket => ticket._id === action.payload.data._id);
          if (index !== -1) {
            state.tickets[index] = action.payload.data;
          }
          if (state.selectedTicket?._id === action.payload.data._id) {
            state.selectedTicket = action.payload.data;
          }
        }
      })
      .addCase(replySupportTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
    
    // Update Status
      .addCase(updateTicketStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Update the ticket status
        if (action.payload?.data) {
          const index = state.tickets.findIndex(ticket => ticket._id === action.payload.data._id);
          if (index !== -1) {
            state.tickets[index] = action.payload.data;
          }
          if (state.selectedTicket?._id === action.payload.data._id) {
            state.selectedTicket = action.payload.data;
          }
        }
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setSelectedTicket, clearSelectedTicket } = supportSlice.actions;
export default supportSlice.reducer;